import mixpanel from 'mixpanel-browser';
import type { User } from '../models/user';
import type { PricingPlan } from '../models/pricing-plan';
import type { Organization } from '../context/organization';

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || '';
const EVENT_NAME_RE = /^[a-z][a-z0-9_]*$/;
const MAX_PARAM_COUNT = 25;
const MAX_STRING_LENGTH = 100;
const LONG_STRING_KEYS = new Set(['page_location', 'page_path', 'page_title']);

let initialized = false;
let currentUserId: string | null = null;
let currentContextKey = '';

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const isAnalyticsEnabled = () => {
  if (!isBrowser()) return false;
  if (!MIXPANEL_TOKEN) return false;
  if (window.location.hostname === 'localhost') return false;
  return true;
};

const sanitizeValue = (key: string, value: unknown): string | number | boolean | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const maxLen = LONG_STRING_KEYS.has(key) ? 300 : MAX_STRING_LENGTH;
    return trimmed.length > maxLen ? trimmed.slice(0, maxLen) : trimmed;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return value;
  }
  if (typeof value === 'boolean') return value;
  return null;
};

const sanitizeContextValue = (key: string, value: unknown): string | number | boolean | null => {
  if (Array.isArray(value)) {
    const compact = value
      .filter((item) => ['string', 'number', 'boolean'].includes(typeof item))
      .map((item) => String(item));
    if (compact.length === 0) return null;
    const joined = compact.join(',');
    const maxLen = LONG_STRING_KEYS.has(key) ? 300 : MAX_STRING_LENGTH;
    return joined.length > maxLen ? joined.slice(0, maxLen) : joined;
  }
  return sanitizeValue(key, value);
};

const sanitizeParams = (params?: Record<string, unknown>) => {
  const cleaned: Record<string, string | number | boolean> = {};
  if (!params) return cleaned;
  for (const [key, value] of Object.entries(params)) {
    if (Object.keys(cleaned).length >= MAX_PARAM_COUNT) break;
    const sanitized = sanitizeValue(key, value);
    if (sanitized === null) continue;
    cleaned[key] = sanitized;
  }
  return cleaned;
};

const titleFromPath = (path: string): string => {
  const clean = path.replace(/^\/+|\/+$/g, '');
  if (!clean) return 'Home';
  return clean
    .split('/')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' - ');
};

export const initializeAnalytics = () => {
  if (initialized || !isAnalyticsEnabled()) return;

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: false,
    track_pageview: false,
    autocapture: false,
    persistence: 'localStorage',
    record_sessions_percent: 0,
    record_heatmap_data: false,
    api_host: 'https://api-eu.mixpanel.com'
  });

  initialized = true;
};

export const setUserId = (userId?: string | null) => {
  if (!isAnalyticsEnabled()) return;
  const cleaned = sanitizeValue('user_id', userId ?? '');
  if (!cleaned || typeof cleaned !== 'string') return;
  if (cleaned === currentUserId) return;
  initializeAnalytics();
  currentUserId = cleaned;
  mixpanel.identify(cleaned);
};

const buildUserContext = (user?: User | null, auth0User?: { email?: string | null; name?: string | null }) => {
  const props: Record<string, string | number | boolean> = {};
  if (!user && !auth0User) return props;

  const entries: Record<string, unknown> = {
    user_id: user?.id,
    user_email: user?.email ?? auth0User?.email,
    user_name: user?.name ?? auth0User?.name,
    user_role: user?.role,
    user_joined_at: user?.date_created,
  };

  for (const [key, value] of Object.entries(entries)) {
    const sanitized = sanitizeContextValue(key, value);
    if (sanitized !== null) props[key] = sanitized;
  }

  return props;
};

const buildOrganizationContext = (organization?: Organization | null, currentPlan?: PricingPlan | null) => {
  const props: Record<string, string | number | boolean> = {};
  if (!organization && !currentPlan) return props;

  const entries: Record<string, unknown> = {
    org_id: organization?.id,
    org_name: organization?.name,
    org_emails: organization?.emails,
    org_created_at: organization?.date_created,
    org_credits: organization?.credits,
    org_data_export_allowed: organization?.data_export_allowed,
    org_acled_countries: organization?.acled_countries,
    org_has_all_countries: organization?.has_all_countries,
    org_has_sdk_license: organization?.has_sdk_license,
    org_sdk_domain_whitelist: organization?.sdk_domain_whitelist
  };

  for (const [key, value] of Object.entries(entries)) {
    const sanitized = sanitizeContextValue(key, value);
    if (sanitized !== null) props[key] = sanitized;
  }

  return props;
};

const buildAppContext = (accountLinked?: boolean | null) => {
  const props: Record<string, string | number | boolean> = {};
  const entries: Record<string, unknown> = {
    app_name: 'civapi-dashboard-ui',
    app_env: process.env.NODE_ENV,
    account_linked: accountLinked ?? undefined,
    hostname: isBrowser() ? window.location.hostname : undefined,
  };
  for (const [key, value] of Object.entries(entries)) {
    const sanitized = sanitizeContextValue(key, value);
    if (sanitized !== null) props[key] = sanitized;
  }
  return props;
};

export const setAnalyticsContext = (params: {
  user?: User | null;
  auth0User?: { email?: string | null; name?: string | null; sub?: string | null };
  organization?: Organization | null;
  currentPlan?: PricingPlan | null;
  accountLinked?: boolean | null;
}) => {
  if (!isAnalyticsEnabled()) return;
  initializeAnalytics();

  const userId = params.user?.id ?? params.auth0User?.sub ?? null;
  const hasUserId = Boolean(userId);
  if (hasUserId && userId) setUserId(userId);

  const userContext = buildUserContext(params.user, params.auth0User);
  const orgContext = buildOrganizationContext(params.organization, params.currentPlan);
  const appContext = buildAppContext(params.accountLinked ?? undefined);
  const context = { ...appContext, ...userContext, ...orgContext };

  const contextKey = JSON.stringify(context);
  if (contextKey !== currentContextKey) {
    currentContextKey = contextKey;
    mixpanel.register(context);
  }

  if (hasUserId && Object.keys(userContext).length > 0) {
    const people: Record<string, string | number | boolean> = { ...userContext, ...orgContext };
    if (userContext.user_email) people.$email = userContext.user_email;
    if (userContext.user_name) people.$name = userContext.user_name;
    mixpanel.people.set(people);
  }
};

export const trackEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (!EVENT_NAME_RE.test(eventName)) return;
  if (!isAnalyticsEnabled()) return;
  initializeAnalytics();
  const payload = sanitizeParams(params);
  mixpanel.track(eventName, payload);
};

export const trackPageView = (path: string, params?: Record<string, unknown>) => {
  if (!isBrowser()) return;
  const payload = {
    page_path: path,
    page_location: window.location.href,
    page_title: titleFromPath(path),
    ...params,
  };
  trackEvent('page_view', payload);
};

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;
